import { PricingStrategyEnum } from '@fhs/loyalty/src/types';
import { MenuObjectTypes, OfferDiscountTypes } from '@fhs-legacy/frontend/src/enums/menu';
import { IIncentiveSelection } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { actions } from '@fhs-legacy/frontend/src/state/global-state';
import { AppDispatch } from '@fhs-legacy/frontend/src/state/global-state/store';
import {
  IAppliedRewards,
  IUseGetAvailableRewards,
  IncentiveEligibleItem,
} from '@fhs-legacy/frontend/src/state/loyalty/hooks/types';
import { LoyaltyOffer } from '@fhs-legacy/frontend/src/state/loyalty/types';
import { OffersValue } from '@fhs-legacy/frontend/src/state/offers';
import { dollarsToCents } from '@fhs-legacy/frontend/src/utils';
import { ICartEntry, IDiscount, IItemGroupDiscount, IOfferDiscount } from '@rbi-ctg/menu';

import { computeCartTotal } from '../pricing-cart/compute-cart-total';

export interface ILoyaltyInfo {
  loyaltyEnabled: boolean;
  appliedLoyaltyRewards?: IAppliedRewards;
  appliedLoyaltyOfferDiscount?: IOfferDiscount | null | undefined;
  offersEligibleItems?: IncentiveEligibleItem[];
  loyaltySelectedOffer?: LoyaltyOffer;
}

export function shouldOfferV2BeRemoved(
  item: ICartEntry,
  selectedOfferSelections: IIncentiveSelection[]
) {
  // also required in getOfferText/removeItemMessage
  const isASelectionFromOffer = selectedOfferSelections?.find(
    selection => selection.lineId === item.cartId
  );
  return isASelectionFromOffer;
}

export function shouldOfferBeRemoved(
  currentEntries: ICartEntry[],
  item: ICartEntry,
  offers: OffersValue,
  loyaltyEnabled: boolean,
  appliedLoyaltyRewards: IAppliedRewards,
  discountAppliedCmsOffers: any
) {
  const cartEntriesWithRemovedItem = currentEntries.filter(
    (entry: ICartEntry) => entry._id !== item?._id
  );
  const offerDetails = {
    selectedOffer: offers.selectedOffer,
    selectedOfferCartEntry: offers.selectedOfferCartEntry,
    selectedOfferPrice: offers.selectedOfferPrice,
  };
  const loyaltyInfo = {
    loyaltyEnabled,
    appliedLoyaltyRewards,
    appliedLoyaltyOfferDiscount: discountAppliedCmsOffers?.incentives?.[0] as IOfferDiscount,
  };
  let total = computeCartTotal(offerDetails, cartEntriesWithRemovedItem, loyaltyInfo);

  total = total < 0 ? 0 : total;

  const offerPrice = (offers?.selectedOffer?.option as IOfferDiscount)?.discountValue || 0;
  // TODO: this line does not account for percentage type discounts
  return total < offerPrice * 100;
}

export function removeOfferFromCart(offers: OffersValue) {
  const { selectedOffer: selectedCBAOffer, clearSelectedOffer } = offers;

  return selectedCBAOffer && clearSelectedOffer();
}

export function removeRewardIfNeeded(
  cartEntry: ICartEntry,
  dispatch: AppDispatch,
  getAvailableRewardFromCartEntry: IUseGetAvailableRewards['getAvailableRewardFromCartEntry'],
  appliedLoyaltyRewards: IAppliedRewards,
  loyaltyEnabled: boolean
) {
  const { cartId } = cartEntry;
  const cartEntryReward = getAvailableRewardFromCartEntry(cartEntry);

  const isRewardApplied = !!appliedLoyaltyRewards?.[cartId]?.timesApplied;
  if (loyaltyEnabled && isRewardApplied && cartEntryReward) {
    dispatch(
      actions.loyalty.removeAppliedReward({
        rewardBenefitId: cartEntryReward.rewardBenefitId,
        cartId,
      })
    );
  }
}

export const getLoyaltyOfferDiscount = ({
  loyaltyEnabled,
  appliedLoyaltyOfferDiscount,
  loyaltySelectedOffer,
}: ILoyaltyInfo) => {
  // return if there no offer selected
  if (!loyaltySelectedOffer) {
    return;
  }
  return loyaltyEnabled && isOfferDiscount(appliedLoyaltyOfferDiscount)
    ? appliedLoyaltyOfferDiscount
    : undefined;
};

export const isOfferDiscount = (option: any): option is IOfferDiscount =>
  option?._type === MenuObjectTypes.OFFER_DISCOUNT;

interface ICartEntryForPricing extends Pick<ICartEntry, 'price' | 'quantity' | 'cartId'> {}

export const getIndividualItemPrice = ({
  cartEntry,
}: {
  cartEntry: ICartEntryForPricing;
}): number => (cartEntry.price || 0) / cartEntry.quantity;

export const getItemLoyaltyRewardDiscount = ({
  cartEntry,
}: {
  cartEntry: ICartEntryForPricing;
}) => {
  // not used in fresh/cart anymore
  const individualItemPrice = getIndividualItemPrice({ cartEntry });
  const timesRewardApplied = 1;
  return individualItemPrice * timesRewardApplied;
};

const getFinalDiscount = (price: number, discount: number) =>
  price - discount < 0 ? price : discount;

const calculateItemGroupDiscount = ({
  discountType,
  discountValue,
  itemPrice,
}: {
  discountType: OfferDiscountTypes;
  discountValue: number;
  itemPrice: number;
}) => {
  if (discountType === OfferDiscountTypes.AMOUNT) {
    const discountValueInCents = Math.abs(dollarsToCents(discountValue));
    return getFinalDiscount(itemPrice, discountValueInCents);
  }

  if (discountType === OfferDiscountTypes.PERCENTAGE) {
    const discountPercentage = discountValue / 100;
    // If the discount percentage is over 100% the order is free...
    if (discountPercentage >= 1) {
      return itemPrice;
    }
    const discountValueInCents = Math.round(itemPrice * discountPercentage);

    return discountValueInCents;
  }

  return 0;
};

export const getItemOfferDiscount = ({
  cartEntry,
  offer,
  eligibleItem,
}: {
  cartEntry: ICartEntryForPricing;
  offer: LoyaltyOffer;
  eligibleItem: IncentiveEligibleItem;
}) => {
  const { timesApplied, savingsAmount } = eligibleItem;
  if (savingsAmount) {
    return savingsAmount;
  }
  const individualItemPrice = getIndividualItemPrice({ cartEntry });
  let discount = 0;

  offer?.incentives?.forEach(incentive => {
    switch (incentive?._type) {
      case MenuObjectTypes.ITEM_GROUP_DISCOUNT:
      case PricingStrategyEnum.DISCOUNT: {
        const appliedIncentive = incentive as IItemGroupDiscount;
        const discountType = appliedIncentive.discountType;
        const discountValue = appliedIncentive.discountValue;

        if (discountType && discountValue) {
          discount += calculateItemGroupDiscount({
            discountType,
            discountValue,
            itemPrice: individualItemPrice,
          });
        }

        break;
      }
    }
  });

  return discount * timesApplied;
};

export const getCartEntryBenefit = (
  cartEntries: ICartEntry[],
  getAvailableRewardFromCartEntry: (cartEntry: ICartEntry) => void,
  rewardBenefitId: string | undefined
) =>
  cartEntries?.find(entry => {
    const { _id } = entry ?? {};
    // in the case of pickers, the `_id` won't match `rewardBenefitId`
    // however we can derive the resolved item/combo using the `url` property on the cartEntry
    return _id === rewardBenefitId || getAvailableRewardFromCartEntry(entry);
  });

export const computeTotalDiscountAmount = (allDiscounts?: IDiscount[]) => {
  if (!allDiscounts) {
    return 0;
  }
  return allDiscounts.reduce((acc, curr) => curr.value + acc, 0);
};

export const computeDeliveryFee = ({
  feeCents,
  feeDiscountCents,
  serviceFeeCents,
  smallCartFeeCents,
  geographicalFeeCents,
}: {
  feeCents: number | null;
  feeDiscountCents: number | null;
  serviceFeeCents: number | null;
  smallCartFeeCents: number | null;
  geographicalFeeCents: number | null;
}) => {
  // The value "feeCents" includes the additional fees "serviceFeeCents" and "smallCartFeeCents"
  return Math.max(
    (feeCents || 0) -
      (feeDiscountCents || 0) -
      (serviceFeeCents || 0) -
      (smallCartFeeCents || 0) -
      (geographicalFeeCents || 0),
    0
  );
};
